import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Check, AlertTriangle, Upload } from "lucide-react";
import { toast } from "sonner";
import { parseEvidencePack } from "@/lib/api";
import { periodLabel } from "@/lib/dates";

/**
 * "Make this audit decision-grade" — optional evidence packs.
 *
 * Deliberately collapsed and additive: a buyer can submit the Core Margin Audit
 * without opening any of this. Each pack, when supplied and sufficient, unlocks
 * a class of recommendation; when absent the audit says the recommendation was
 * not evaluated and names the smallest test, rather than guessing.
 *
 * The commerce platform is asked ONLY inside the Pricing card and never
 * assumed — a non-Shopify export is never labelled a Shopify report. Date ranges
 * are computed from today (see lib/dates), never hard-coded.
 *
 * Nothing here parses figures in the browser: each file is sent to the
 * deterministic backend parser, which returns aggregates for confirmation. Raw
 * files are never stored.
 */

const PLATFORMS = [
  { id: "shopify", label: "Shopify" },
  { id: "woocommerce", label: "WooCommerce" },
  { id: "bigcommerce", label: "BigCommerce" },
  { id: "wix_squarespace", label: "Wix / Squarespace" },
  { id: "amazon_marketplace", label: "Amazon / marketplace-led" },
  { id: "other", label: "Other ecommerce platform" },
  { id: "cannot_export", label: "I have a website but cannot export this data" },
];

const MARKETING_CHANNELS = [
  { id: "meta", label: "Meta Ads" },
  { id: "google", label: "Google Ads" },
  { id: "tiktok", label: "TikTok Ads" },
  { id: "other", label: "Other paid channel" },
  { id: "none", label: "No paid media in this period" },
];

// Plain-language column lists, revealed only on "What should this export contain?".
const REQUESTED_COLUMNS = {
  meta_campaign: [
    "Date (one row per campaign per day)", "Campaign ID", "Campaign name", "Objective",
    "Delivery/status", "Amount spent", "Impressions", "Reach", "Frequency",
    "Link clicks or outbound clicks", "Landing-page views (if available)", "Purchases",
    "Purchase conversion value / revenue", "Cost per purchase", "Purchase ROAS",
    "Attribution setting/window (if available)",
  ],
  meta_adset: [
    "Date (one row per ad set per day)", "Campaign ID and name", "Ad set ID and name",
    "Audience / targeting label (if available)", "Placement breakdown (if available)",
    "Amount spent", "Impressions", "Reach", "Frequency", "Link/outbound clicks",
    "Landing-page views (if available)", "Purchases", "Purchase conversion value",
    "Cost per purchase", "Purchase ROAS", "Attribution setting/window (if available)",
  ],
  google_ads: [
    "Date (one row per campaign per day)", "Campaign ID and name", "Campaign type",
    "Campaign status", "Spend / cost", "Impressions", "Clicks", "CTR", "Average CPC",
    "Conversions (purchases)", "Conversion value", "Value / cost",
    "Conversion action (if available)", "Brand vs non-brand label (if used)",
  ],
  commerce_daily_sales: [
    "Date", "Orders", "Units sold", "Gross sales", "Discounts", "Refunds / returns",
    "Net sales", "Shipping revenue (if available)", "Currency", "Sales channel/source (if available)",
  ],
  ga4: [
    "Date", "Default channel group / source-medium / campaign", "Sessions",
    "Engaged sessions", "Ecommerce purchases", "Purchase revenue", "Session conversion rate",
  ],
  product_variant_weekly: [
    "Week start date (one row per SKU/variant per week)", "Product title", "Variant title",
    "SKU (if used)", "Product category (if available)", "Units sold", "Gross sales",
    "Discount amount", "Refund amount", "Net sales",
    "Realised average selling price (or we derive it from net ÷ units and label it derived)",
    "COGS per unit (if available)", "Inventory available / days in stock (if available)",
    "Days out of stock / stockout flag (if available)", "Sales channel (if available)",
  ],
  price_promo_history: [
    "Event date", "SKU or variant identifier", "Product / variant name", "Previous list price",
    "New list price", "Previous sale price (if applicable)", "New sale price (if applicable)",
    "Discount code / promotion name (if applicable)", "Promotion start and end date (if applicable)",
    "Reason for price change (if available)",
  ],
  retention_cohort: [
    "Cohort month or first-purchase month", "New customers", "Returning customers", "Orders",
    "Revenue", "Refunds (if available)",
    "Repeat orders within 30 / 90 / 180 / 365 days (where available)", "Currency",
  ],
  klaviyo_campaign: [
    "Date", "Campaign/flow name", "Sends", "Opens/clicks (if available)", "Placed orders",
    "Attributed revenue", "Attribution window (if available)",
  ],
};

const platformLabel = (id) => (PLATFORMS.find((p) => p.id === id) || {}).label || "your platform";

function ColumnsToggle({ source }) {
  const [open, setOpen] = useState(false);
  const cols = REQUESTED_COLUMNS[source] || [];
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((v) => !v)} className="tick text-[#9a3412]">
        {open ? "Hide columns" : "What should this export contain?"}
      </button>
      {open && (
        <ul className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1">
          {cols.map((c, i) => (
            <li key={i} className="font-mono-tech text-[11px] text-[#525252] flex gap-1.5">
              <span className="text-[#9a3412]">·</span><span>{c}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One upload slot for a single source. */
function UploadCard({ sessionId, source, title, period, platform, channel, onParsed, onRemove, entry }) {
  const [busy, setBusy] = useState(false);
  const [declined, setDeclined] = useState(null); // "unavailable" | "not_used"

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(pdf|xlsx|csv)$/i.test(file.name)) {
      toast.error("Upload a CSV, .xlsx or PDF export.");
      e.target.value = "";
      return;
    }
    try {
      setBusy(true);
      setDeclined(null);
      const parsed = await parseEvidencePack(file, { source, platform, channel }, sessionId);
      onParsed(parsed);
      if (parsed.requires_manual_confirmation) {
        toast("Saved for the analyst to confirm by hand — a CSV/XLSX gives a stronger read.");
      } else if (parsed.missing_fields?.length) {
        toast("Read it, but some expected columns are missing — see the note below. You can still submit.");
      } else {
        toast.success(`Read ${parsed.filename}: ${parsed.matched_fields?.length || 0} fields detected.`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not read that file. You can still submit without it.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="border border-[#ddd8ce] rounded-[6px] p-4 bg-[#faf8f4]" data-testid={`evidence-upload-${source}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="font-mono-tech text-[12px] text-[#171717] font-medium">{title}</div>
        {period && <span className="tick text-[#525252] shrink-0">{period}</span>}
      </div>
      <ColumnsToggle source={source} />

      {entry ? (
        <div className="mt-3 border-t border-[#ddd8ce] pt-3">
          <div className="font-mono-tech text-[12px] text-[#3f6b4a] flex items-center gap-2">
            <Check size={13} /> {entry.filename}
            {entry.requires_manual_confirmation && <span className="text-[#8a5a3a]">· to confirm by hand</span>}
          </div>
          {!entry.requires_manual_confirmation && (
            <div className="mt-2 font-mono-tech text-[11px] text-[#525252] space-y-1">
              {entry.period_start && (
                <div>Covers {entry.period_start} → {entry.period_end}
                  {entry.period_complete === false && <span className="text-[#9a3412]"> · does not fully cover the requested window</span>}
                </div>
              )}
              {!!entry.missing_fields?.length && (
                <div className="text-[#9a3412] flex items-start gap-1.5">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  <span>Missing: {entry.missing_fields.join(", ")}. Not blocking — the audit will note what this leaves unevaluated.</span>
                </div>
              )}
              {(entry.warnings || []).map((w, i) => (
                <div key={i} className="text-[#8a5a3a]">{w}</div>
              ))}
            </div>
          )}
          <button type="button" onClick={onRemove} className="mt-2 tick text-[#9a3412]">Remove</button>
        </div>
      ) : declined ? (
        <div className="mt-3 font-mono-tech text-[11px] text-[#525252]">
          Noted — {declined === "unavailable" ? "not available for this period" : "you don't use this source"}.
          <button type="button" onClick={() => setDeclined(null)} className="ml-2 tick text-[#9a3412]">Undo</button>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="btn-secondary inline-flex cursor-pointer text-[12px]">
            {busy ? <><Loader2 size={13} className="animate-spin" /> Reading…</> : <><Upload size={13} /> Upload CSV / XLSX / PDF</>}
            <input type="file" accept=".pdf,.xlsx,.csv" className="hidden" onChange={handle} disabled={busy} />
          </label>
          <button type="button" onClick={() => setDeclined("unavailable")} className="tick text-[#525252]">Not available</button>
          <button type="button" onClick={() => setDeclined("not_used")} className="tick text-[#525252]">I don't use this source</button>
        </div>
      )}
    </div>
  );
}

function PackCard({ id, title, unlocks, children, open, onToggle }) {
  return (
    <div className="border border-[#ddd8ce] rounded-[6px] overflow-hidden" data-testid={`evidence-card-${id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-4 text-left bg-[#f5f2ed] hover:bg-[#efece5] transition-colors"
        data-testid={`evidence-toggle-${id}`}
      >
        <div>
          <div className="font-mono-tech text-[13px] text-[#171717] font-medium">{title}</div>
          <div className="font-mono-tech text-[11px] text-[#525252] mt-0.5">{unlocks}</div>
        </div>
        {open ? <ChevronDown size={15} className="shrink-0 text-[#9a3412]" /> : <ChevronRight size={15} className="shrink-0 text-[#525252]" />}
      </button>
      {open && <div className="p-4 space-y-4 border-t border-[#ddd8ce]">{children}</div>}
    </div>
  );
}

const ATTRIBUTION_NOTE =
  "Meta and other ad platforms report platform-attributed purchases. We will not treat these as incremental sales unless you provide an incrementality, holdout, geo-lift or documented spend-change result.";

export default function EvidencePacks({ sessionId, onChange }) {
  const [openCard, setOpenCard] = useState(null);
  const [platform, setPlatform] = useState("");
  const [channels, setChannels] = useState([]);
  const [entries, setEntries] = useState({}); // source(+channel) key -> parsed entry
  const [declarations, setDeclarations] = useState({});

  const period6 = useMemo(() => periodLabel(6), []);
  const period12 = useMemo(() => periodLabel(12), []);

  // Bubble the assembled, versioned structure up to the intake form.
  const emit = (nextEntries, nextPlatform, nextChannels, nextDecl) => {
    const packs = Object.values(nextEntries).filter(Boolean);
    onChange?.({
      version: 1,
      platform: nextPlatform || undefined,
      marketing_channels: nextChannels.length ? nextChannels : undefined,
      declarations: nextDecl,
      packs,
    });
  };

  const setEntry = (key, entry) => {
    setEntries((prev) => {
      const next = entry ? { ...prev, [key]: entry } : (() => { const n = { ...prev }; delete n[key]; return n; })();
      emit(next, platform, channels, declarations);
      return next;
    });
  };

  const toggleChannel = (id) => {
    setChannels((prev) => {
      let next;
      if (id === "none") next = prev.includes("none") ? [] : ["none"];
      else next = prev.includes(id) ? prev.filter((c) => c !== id) : [...prev.filter((c) => c !== "none"), id];
      emit(entries, platform, next, declarations);
      return next;
    });
  };

  const setDecl = (k, v) => {
    setDeclarations((prev) => {
      const next = { ...prev, [k]: v };
      emit(entries, platform, channels, next);
      return next;
    });
  };

  const onPlatform = (v) => {
    setPlatform(v);
    emit(entries, v, channels, declarations);
  };

  const commerceLabel = platform === "shopify"
    ? "Shopify daily sales export"
    : `The equivalent daily sales export from ${platformLabel(platform || "other")}`;

  const showMeta = channels.includes("meta");
  const showGoogle = channels.includes("google");
  const anyPaid = channels.some((c) => ["meta", "google", "tiktok", "other"].includes(c));

  return (
    <div className="pt-4 border-t border-[#ddd8ce]" data-testid="evidence-packs">
      <div className="tick text-[#9a3412] mb-1">Make this audit decision-grade (optional)</div>
      <p className="font-mono-tech text-[12px] text-[#525252] mb-3 max-w-2xl">
        Add evidence only for the decisions you want us to assess. You can still submit the core
        audit without these files.
      </p>
      {/* Mirrors "The exports you upload for an evidence pack" in
          legal/data-methodology.md v1.2.0. Change one, change both. */}
      <p className="font-mono-tech text-[12px] text-[#525252] mb-5 max-w-2xl" data-testid="evidence-retention-note">
        We read each file once and do not keep it. We keep a summary: the file's totals, and the
        largest campaigns and products by the names your own systems use, so the audit can tell you
        which ones. Those names are never shown to another client, never used in a benchmark and
        never published.{" "}
        <a href="/legal/methodology" target="_blank" rel="noopener noreferrer" className="underline text-[#9a3412]">
          What we keep, in full
        </a>
        .
      </p>

      <div className="space-y-3">
        {/* ---------------- Marketing ---------------- */}
        <PackCard
          id="marketing"
          title="Marketing spend decisions"
          unlocks="Unlocks channel-level spend, reallocation, scaling and ROAS recommendations."
          open={openCard === "marketing"}
          onToggle={() => setOpenCard(openCard === "marketing" ? null : "marketing")}
        >
          <p className="font-mono-tech text-[11px] text-[#525252]">
            Reporting period: <strong>last 6 completed calendar months</strong> ({period6}).
            Accepted formats: CSV or XLSX (PDF accepted but confirmed by hand).
          </p>
          <p className="font-mono-tech text-[11px] text-[#8a5a3a] bg-[rgba(138,90,58,0.08)] p-2 rounded">
            {ATTRIBUTION_NOTE}
          </p>

          <div>
            <div className="font-mono-tech text-[12px] text-[#171717] mb-2">
              Which paid channels had spend in the last 6 completed calendar months?
            </div>
            <div className="flex flex-wrap gap-3">
              {MARKETING_CHANNELS.map((c) => (
                <label key={c.id} className="flex items-center gap-2 font-mono-tech text-[12px] text-[#404040]">
                  <input type="checkbox" checked={channels.includes(c.id)} onChange={() => toggleChannel(c.id)}
                         data-testid={`channel-${c.id}`} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>

          {showMeta && (
            <>
              <UploadCard sessionId={sessionId} source="meta_campaign" channel="meta"
                title="Meta — campaign performance (one row per campaign per day)" period={period6}
                entry={entries.meta_campaign} onParsed={(e) => setEntry("meta_campaign", e)}
                onRemove={() => setEntry("meta_campaign", null)} />
              <UploadCard sessionId={sessionId} source="meta_adset" channel="meta"
                title="Meta — ad-set performance (one row per ad set per day)" period={period6}
                entry={entries.meta_adset} onParsed={(e) => setEntry("meta_adset", e)}
                onRemove={() => setEntry("meta_adset", null)} />
            </>
          )}
          {showGoogle && (
            <UploadCard sessionId={sessionId} source="google_ads" channel="google"
              title="Google Ads — daily campaign performance" period={period6}
              entry={entries.google_ads} onParsed={(e) => setEntry("google_ads", e)}
              onRemove={() => setEntry("google_ads", null)} />
          )}
          {anyPaid && (
            <UploadCard sessionId={sessionId} source="commerce_daily_sales" platform={platform}
              title={commerceLabel + " — daily, same 6-month window"} period={period6}
              entry={entries.commerce_daily_sales} onParsed={(e) => setEntry("commerce_daily_sales", e)}
              onRemove={() => setEntry("commerce_daily_sales", null)} />
          )}
          {anyPaid && (
            <UploadCard sessionId={sessionId} source="ga4"
              title="GA4 daily acquisition export — optional but useful" period={period6}
              entry={entries.ga4} onParsed={(e) => setEntry("ga4", e)}
              onRemove={() => setEntry("ga4", null)} />
          )}
          {anyPaid && (
            <label className="flex items-start gap-2 font-mono-tech text-[11px] text-[#404040]">
              <input type="checkbox" checked={!!declarations.incrementality_available}
                     onChange={(e) => setDecl("incrementality_available", e.target.checked)}
                     data-testid="decl-incrementality" />
              <span>I can provide a holdout, geo-lift, lift test or documented historical spend-change
                result (the analyst will confirm it). Without this, any spend change is proposed as a
                staged test, not a revenue forecast.</span>
            </label>
          )}
        </PackCard>

        {/* ---------------- Pricing ---------------- */}
        <PackCard
          id="pricing"
          title="Pricing and promotion decisions"
          unlocks="Unlocks candidate SKUs and a controlled price-test design; a price-outcome forecast still needs a test result."
          open={openCard === "pricing"}
          onToggle={() => setOpenCard(openCard === "pricing" ? null : "pricing")}
        >
          <p className="font-mono-tech text-[11px] text-[#525252]">
            Reporting period: <strong>last 12 completed calendar months</strong> ({period12}).
            Accepted formats: CSV or XLSX (PDF accepted but confirmed by hand).
          </p>

          <div>
            <label className="tick block mb-2">Which commerce platform do you sell on?</label>
            <select className="input-vault" value={platform} onChange={(e) => onPlatform(e.target.value)}
                    data-testid="pricing-platform">
              <option value="">Select…</option>
              {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <p className="font-mono-tech text-[11px] text-[#525252] mt-1">
              We ask so we request the right export — we don't assume Shopify.
            </p>
          </div>

          {platform && platform !== "cannot_export" && (
            <>
              <UploadCard sessionId={sessionId} source="product_variant_weekly" platform={platform}
                title={`Product / variant weekly performance from ${platformLabel(platform)} (one row per SKU/variant per week)`}
                period={period12}
                entry={entries.product_variant_weekly} onParsed={(e) => setEntry("product_variant_weekly", e)}
                onRemove={() => setEntry("product_variant_weekly", null)} />
              <UploadCard sessionId={sessionId} source="price_promo_history" platform={platform}
                title={`Price and promotion history from ${platformLabel(platform)} (one row per price/promo event)`}
                period={period12}
                entry={entries.price_promo_history} onParsed={(e) => setEntry("price_promo_history", e)}
                onRemove={() => setEntry("price_promo_history", null)} />
              <p className="font-mono-tech text-[11px] text-[#525252]">
                No explicit price-history export? The weekly product performance above, with realised
                price and discounts, is accepted instead. We never request customer names, emails or addresses.
              </p>
            </>
          )}
          {platform === "cannot_export" && (
            <p className="font-mono-tech text-[11px] text-[#8a5a3a]">
              Understood — pricing will be catalogue diagnosis only, and the audit will propose a
              controlled price test rather than a price-increase forecast.
            </p>
          )}
        </PackCard>

        {/* ---------------- Retention ---------------- */}
        <PackCard
          id="retention"
          title="Retention and customer value"
          unlocks="Improves LTV, repeat-purchase and payback analysis."
          open={openCard === "retention"}
          onToggle={() => setOpenCard(openCard === "retention" ? null : "retention")}
        >
          <p className="font-mono-tech text-[11px] text-[#525252]">
            Reporting period: <strong>last 12 completed calendar months</strong> ({period12}).
            Accepted formats: CSV or XLSX (PDF accepted but confirmed by hand).
          </p>
          <UploadCard sessionId={sessionId} source="retention_cohort"
            title="Cohort export or monthly customer summary" period={period12}
            entry={entries.retention_cohort} onParsed={(e) => setEntry("retention_cohort", e)}
            onRemove={() => setEntry("retention_cohort", null)} />
          <UploadCard sessionId={sessionId} source="klaviyo_campaign"
            title="Klaviyo (or equivalent) campaign/flow performance — optional" period={period6}
            entry={entries.klaviyo_campaign} onParsed={(e) => setEntry("klaviyo_campaign", e)}
            onRemove={() => setEntry("klaviyo_campaign", null)} />
        </PackCard>
      </div>
    </div>
  );
}
